import React, { Fragment, useCallback } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button';
import ThumbUpIcon from '@atlaskit/icon/core/thumbs-up';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { SainAction } from '../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../common/controller/analytics/hooks/use-sain-usage';
import { messages } from '../../messages';
import { useAIAnswerContext } from '../ai-answer-context';
import { onDislikeButtonClick } from '../on-dislike-button-click';
import { onLikeButtonClick } from '../on-like-button-click';

import { FeedbackButtonIconWrapper } from './feedback-button-icon-wrapper';
import { Rotate180IconWrapper } from './rotate180-icon-wrapper';

type FeedbackButtonProps = {
	active: boolean;
	onClick: (e: any) => void;
	tooltipContent: string;
	icon: JSX.Element;
	isDisabled?: boolean;
	testId?: string;
	isReadingAids?: boolean;
};

const FeedbackButton = ({
	active,
	isDisabled,
	onClick: onClickHandler,
	tooltipContent,
	testId,
	icon,
	isReadingAids,
}: FeedbackButtonProps) => {
	return (
		<Tooltip position={'top'} content={tooltipContent}>
			{({ onClick: _onClick, ...tooltipProps }) => (
				<Button
					aria-label={tooltipContent}
					spacing={'none'}
					appearance={isDisabled ? 'subtle-link' : 'subtle'}
					iconBefore={
						<FeedbackButtonIconWrapper
							active={active}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={{ cursor: 'pointer' }}
							{...(testId && { 'data-testid': testId })}
							isDisabled={isDisabled || false}
							isReadingAids={isReadingAids || false}
						>
							{icon}
						</FeedbackButtonIconWrapper>
					}
					{...(!isDisabled && { onClick: onClickHandler })}
					{...tooltipProps}
				/>
			)}
		</Tooltip>
	);
};

type FeedbackButtonsProps = {
	setDisplayFlag: (value: boolean) => void;
	setFeedbackModal: (value: boolean) => void;
};

export const FeedbackButtons = ({
	setDisplayFlag,
	setFeedbackModal,
}: FeedbackButtonsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const {
		commonAttributes,
		fireAnalyticsEvent,
		updateContextCache,
		liked,
		setLiked,
		likesDisabled,
		setLikesDisabled,
		isReadingAids,
	} = useAIAnswerContext();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	// Using a single state to represent both like/dislike states
	// true - liked, false - disliked, undefined - neither are selected
	const likeButtonCallback = useCallback(
		(e: any) => {
			e.preventDefault();
			setDisplayFlag(true);
			setLiked(true);
			setLikesDisabled(true);
			updateContextCache({ collapsed: false, liked: true });
			fireAnalyticsEvent(onLikeButtonClick(commonAttributes));
			fireSAINUsageEvent({
				metCriteria: SainAction.POSITIVE_FEEDBACK_PROVIDED,
				commonAttributes,
			});
		},
		[
			setLiked,
			setLikesDisabled,
			fireAnalyticsEvent,
			commonAttributes,
			updateContextCache,
			setDisplayFlag,
			fireSAINUsageEvent,
		],
	);

	const dislikeButtonCallback = useCallback(
		(e: any) => {
			e.preventDefault();
			setFeedbackModal(true);
			setLiked(false);
			setLikesDisabled(true);
			updateContextCache({ collapsed: false, liked: false });
			fireAnalyticsEvent(onDislikeButtonClick(commonAttributes));
			fireSAINUsageEvent({
				metCriteria: SainAction.NEGATIVE_FEEDBACK_PROVIDED,
				commonAttributes,
			});
		},
		[
			setLiked,
			setLikesDisabled,
			fireAnalyticsEvent,
			commonAttributes,
			updateContextCache,
			setFeedbackModal,
			fireSAINUsageEvent,
		],
	);

	return (
		<Fragment>
			<FeedbackButton
				active={liked === true}
				onClick={likeButtonCallback}
				tooltipContent={formatMessage(messages.ai_answer_component_like_button_tooltip)}
				icon={<ThumbUpIcon label="" spacing="spacious" color={token('color.icon')} />}
				isDisabled={liked && likesDisabled}
				testId="ai-answer-like-button"
				isReadingAids={isReadingAids}
			/>
			<FeedbackButton
				active={liked === false}
				onClick={dislikeButtonCallback}
				tooltipContent={formatMessage(messages.ai_answer_component_dislike_button_tooltip)}
				icon={
					<Rotate180IconWrapper>
						<ThumbUpIcon label="" spacing="spacious" color={token('color.icon')} />
					</Rotate180IconWrapper>
				}
				isDisabled={!liked && likesDisabled}
				testId="ai-answer-dislike-button"
				isReadingAids={isReadingAids}
			/>
		</Fragment>
	);
};
